// Iter-207f (Phase 2) — Cabinet Expenses Drive Health Check page.
// Mirrors the SuppliersDriveHealth pattern: 409 drive_parent_missing → modal
// to paste the Drive folder URL/ID, then per-month counters and a batch
// remediate button.
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, RefreshCw, HardDrive, Receipt,
  Settings as SettingsIcon, Wrench,
} from "lucide-react";
import VatBackfillPanel from "../components/VatBackfillPanel";

const STATUS_META = {
  ok: { color: "emerald", label: "OK", emoji: "✅" },
  missing_drive_upload: { color: "amber", label: "À ré-uploader", emoji: "⚠️" },
  legacy_local_only: { color: "red", label: "Legacy (fichier local perdu)", emoji: "❌" },
  no_attachment: { color: "zinc", label: "Sans pièce jointe", emoji: "—" },
};

export default function CabinetExpensesDriveHealth() {
  const { user } = useAuth();
  const isAdmin = user?.role === "CEO" || user?.role === "ADMIN";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remediating, setRemediating] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [parentInput, setParentInput] = useState("");
  const [savingParent, setSavingParent] = useState(false);
  const [parentInfo, setParentInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cabinet-expenses/drive-health-check");
      setData(r.data);
      setShowParentModal(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "drive_parent_missing") {
        setShowParentModal(true);
        try {
          const p = await api.get("/admin/cabinet-expenses-drive-parent");
          setParentInfo(p.data);
        } catch (_) { /* noop */ }
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Échec du Health Check"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveParent = async () => {
    if (!parentInput.trim()) {
      toast.error("Veuillez coller l'URL ou l'ID du dossier Drive parent.");
      return;
    }
    setSavingParent(true);
    try {
      await api.post("/admin/cabinet-expenses-drive-parent", { parent_id_or_url: parentInput.trim() });
      toast.success("Dossier parent enregistré. Lancement du Health Check…");
      setParentInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingParent(false);
    }
  };

  const [retrying, setRetrying] = useState(false);
  const retryPending = async () => {
    setRetrying(true);
    try {
      const r = await api.post("/admin/expenses/receipts/drive-retry");
      const d = r.data || {};
      toast.success(
        `NDF : ${d.ndf_uploaded || 0} uploadé(s) · ${d.ndf_failed || 0} échec(s) — Cabinet : ${d.cabinet_uploaded || 0} uploadé(s) · ${d.cabinet_failed || 0} échec(s)` +
        (d.recovered_stages ? ` · ${d.recovered_stages} fichier(s) récupéré(s)` : ""),
        { duration: 9000 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Échec de la relance");
    } finally {
      setRetrying(false);
    }
  };

  const remediate = async () => {
    if (!data?.remediable_count) {
      toast.info("Aucune ligne à remédier.");
      return;
    }
    if (!window.confirm(`Re-uploader ${data.remediable_count} justificatif(s) cabinet vers Drive ?`)) return;
    setRemediating(true);
    try {
      const r = await api.post("/cabinet-expenses/drive-remediate");
      const { uploaded, skipped_legacy, failed, relink_fixed, broken_link_cleared } = r.data;
      // Iter-207i — show ALL 5 counters so the user understands exactly what
      // happened (esp. the new relink_fixed / broken_link_cleared from the
      // PROD hotfix that backfills legacy lines).
      const parts = [
        `${uploaded || 0} uploadé(s)`,
        `${relink_fixed || 0} relinké(s)`,
        `${broken_link_cleared || 0} lien(s) cassé(s) nettoyé(s)`,
        `${skipped_legacy || 0} legacy ignoré(s)`,
        `${failed || 0} échec(s)`,
      ];
      toast.success(parts.join(" · "));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Échec de la remédiation");
    } finally {
      setRemediating(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="cabinet-drive-health-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <HardDrive size={22} /> Dépenses cabinet — Drive Health Check
        </h1>
        <p className="text-sm text-zinc-600 max-w-3xl leading-relaxed">
          Audite les justificatifs des dépenses cabinet et vérifie qu'ils sont bien archivés sur Google Drive dans la structure <code>{`{parent}/001-Justificatifs/{année}/{mois}/`}</code>. Les nouveaux justificatifs sont automatiquement uploadés à la création.
        </p>
      </header>

      {/* Iter-207bb — Retroactive VAT backfill (CEO/ADMIN only). */}
      {isAdmin && <VatBackfillPanel />}

      {data?.parent_id && (
        <div className="text-xs text-zinc-500 font-mono flex items-center gap-2">
          <SettingsIcon size={12} /> Dossier parent : <code className="text-zinc-700">{data.parent_id}</code>
          <button
            onClick={() => setShowParentModal(true)}
            data-testid="cabinet-drive-change-parent"
            className="text-blue-600 hover:underline"
          >
            modifier
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={load}
          disabled={loading}
          data-testid="cabinet-drive-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-xs font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Relancer
        </button>
        {data && data.remediable_count > 0 && (
          <button
            onClick={remediate}
            disabled={remediating}
            data-testid="cabinet-drive-remediate-all"
            className="inline-flex items-center gap-1.5 px-3 h-8 border border-blue-200 bg-blue-50 text-blue-700 text-xs font-mono uppercase tracking-wider rounded-md hover:bg-blue-100 disabled:opacity-50"
          >
            {remediating ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Remédier ({data.remediable_count})
          </button>
        )}
        {/* Iter-292 — push every receipt (NDF perso + cabinet) still only staged to Drive now. */}
        <button
          onClick={retryPending}
          disabled={retrying}
          data-testid="receipts-drive-retry-now"
          title="Relance immédiate de l'upload Drive des justificatifs NDF + cabinet en attente (sinon automatique toutes les 15 min)"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-amber-200 bg-amber-50 text-amber-800 text-xs font-mono uppercase tracking-wider rounded-md hover:bg-amber-100 disabled:opacity-50"
        >
          {retrying ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Pousser les justificatifs en attente
        </button>
        {data && (
          <div className="ml-auto text-xs text-zinc-500 font-mono" data-testid="cabinet-drive-scanned">
            {data.scanned} ligne(s) scannée(s)
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.counters || {}).map(([k, v]) => {
            const meta = STATUS_META[k] || { color: "zinc", label: k };
            return (
              <div key={k} className="p-3 rounded-md border border-zinc-100 bg-white" data-testid={`cabinet-drive-counter-${k}`}>
                <div className={`text-[10px] font-mono uppercase tracking-wider text-${meta.color}-700`}>{meta.emoji} {meta.label}</div>
                <div className="text-xl font-light text-zinc-800 mt-1">{v}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-month breakdown */}
      <div className="border border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600 font-mono uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Période</th>
              <th className="px-3 py-2 text-right">OK</th>
              <th className="px-3 py-2 text-right">À ré-uploader</th>
              <th className="px-3 py-2 text-right">Legacy</th>
              <th className="px-3 py-2 text-right">Sans PJ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(!data?.per_month || data.per_month.length === 0) && (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-zinc-500">
                {loading ? "Chargement…" : "Aucune dépense cabinet enregistrée."}
              </td></tr>
            )}
            {data?.per_month?.map((m) => (
              <tr key={`${m.year}-${m.month}`} data-testid={`cabinet-drive-month-${m.year}-${m.month}`}>
                <td className="px-3 py-2 font-medium text-zinc-800 flex items-center gap-1.5">
                  <Receipt size={11} className="text-zinc-400" />
                  {m.month.toString().padStart(2, "0")}/{m.year}
                </td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{m.ok || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{m.missing_drive_upload || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-red-700">{m.legacy_local_only || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-500">{m.no_attachment || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Itemized list of non-OK lines */}
      {data?.items?.length > 0 && (
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <div className="bg-zinc-50 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-600">
            Détail des lignes non conformes ({data.items.length})
          </div>
          <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-xs">
            <thead className="bg-white text-zinc-500 font-mono uppercase text-[10px] tracking-wider border-b border-zinc-100">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Fournisseur</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2 text-left">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.items.slice(0, 100).map((it) => {
                const meta = STATUS_META[it.status] || { color: "zinc", label: it.status };
                return (
                  <tr key={it.cabinet_line_id} data-testid={`cabinet-drive-line-${it.cabinet_line_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-600">{it.invoice_date}</td>
                    <td className="px-3 py-2 text-zinc-800">{it.vendor}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-700">{Number(it.amount || 0).toFixed(2)} {it.currency}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-${meta.color}-200 bg-${meta.color}-50 text-${meta.color}-700`}>
                        {meta.emoji} {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.items.length > 100 && (
                <tr><td colSpan={4} className="px-3 py-2 text-center text-zinc-500 italic">… {data.items.length - 100} ligne(s) supplémentaire(s) tronquée(s)</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* First-run / change-parent modal */}
      {showParentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="cabinet-drive-parent-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <SettingsIcon size={18} /> Dossier Drive parent — Dépenses cabinet
            </h2>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Collez l'URL Google Drive du dossier parent (ou son ID brut). Les justificatifs seront archivés en sous-dossier <code>{`001-Justificatifs/{année}/{mois}/`}</code>.
            </p>
            {parentInfo?.parent_id && (
              <div className="bg-zinc-50 rounded p-2 text-[11px] font-mono text-zinc-600">
                Actuel : {parentInfo.parent_id}
              </div>
            )}
            <input
              type="text"
              value={parentInput}
              onChange={(e) => setParentInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              data-testid="cabinet-drive-parent-input"
              className="w-full border border-zinc-200 rounded px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              {data?.parent_id && (
                <button onClick={() => setShowParentModal(false)} className="px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900">
                  Annuler
                </button>
              )}
              <button
                onClick={saveParent}
                disabled={savingParent}
                data-testid="cabinet-drive-parent-save"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50"
              >
                {savingParent ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
